'use client'

import { motion } from 'framer-motion'
import {
  Handshake,
  Package,
  Store,
  Zap,
  DollarSign,
  Globe,
  Bot,
  MousePointerClick,
  Mail,
  MessageCircle,
  ArrowRight,
  Gem,
  Building2,
  ShoppingCart,
  Megaphone,
  UserCheck,
  Target,
  HeartHandshake,
  Rocket,
  Clock,
  BarChart3,
} from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { StarRating } from '@/components/ui/star-rating'

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Handshake,
  Package,
  Store,
  Zap,
  DollarSign,
  Globe,
  Bot,
  MousePointerClick,
  Mail,
  MessageCircle,
  Building2,
  ShoppingCart,
  Megaphone,
  UserCheck,
  Target,
  HeartHandshake,
  Rocket,
  Clock,
  BarChart3,
}

interface AudienceItem {
  icon: string
  title: string
  description: string
}

interface BenefitItem {
  icon: string
  label: string
}

interface ProductShowcaseProps {
  id?: string
  overline: string
  headline: string
  description: string
  benefits?: BenefitItem[]
  audience?: AudienceItem[]
  cta: { text: string; href: string }
  appStore?: { rating: number; reviews: number; href: string }
  badge?: string
  variant?: 'primary' | 'ai'
  reversed?: boolean
  illustration?: React.ReactNode
}

export function ProductShowcase({
  id,
  overline,
  headline,
  description,
  benefits,
  audience,
  cta,
  appStore,
  badge,
  variant = 'primary',
  reversed = false,
  illustration,
}: ProductShowcaseProps) {
  const isAi = variant === 'ai'

  return (
    <Section
      background={reversed ? 'gray' : 'white'}
      className="relative"
    >
      <Container>
        <div id={id} className="scroll-mt-24">
          {/* Card wrapper with border and radius */}
          <div
            className={`relative rounded-3xl border-2 p-6 lg:p-10 overflow-hidden ${
              isAi
                ? 'bg-stone-900 border-stone-800'
                : 'bg-white/50 border-primary-200 backdrop-blur-sm'
            }`}
          >
            {/* Asymmetric thick border accent */}
            <div
              className={`absolute top-0 bottom-0 ${isAi ? 'left-0' : 'right-0'} hidden lg:block`}
              style={{
                width: isAi ? '12px' : '16px',
                background: isAi
                  ? 'linear-gradient(180deg, rgba(34,197,94,0.4) 0%, rgba(34,197,94,0.9) 30%, rgba(34,197,94,1) 50%, rgba(34,197,94,0.9) 70%, rgba(34,197,94,0.4) 100%)'
                  : 'linear-gradient(180deg, rgba(16,185,129,0.4) 0%, rgba(16,185,129,0.9) 30%, rgba(16,185,129,1) 50%, rgba(16,185,129,0.9) 70%, rgba(16,185,129,0.4) 100%)',
              }}
            />

            <div
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-start ${
                reversed ? 'lg:flex-row-reverse' : ''
              }`}
            >
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: reversed ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={reversed ? 'lg:order-2' : ''}
            >
              <div className="mb-4">
                <p className={`text-overline ${isAi ? 'text-green-400' : ''}`}>{overline}</p>
              </div>

              <h2 className={`text-display-1 mb-4 ${isAi ? 'text-white' : 'text-stone-900'}`}>
                {headline}
              </h2>

              <p className={`text-body-lg mb-8 ${isAi ? 'text-stone-300' : ''}`}>{description}</p>

              {/* Benefits */}
              {benefits && benefits.length > 0 && (
                <div className="mb-8">
                  <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${
                    isAi ? 'text-stone-500' : 'text-stone-400'
                  }`}>
                    Benefits
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {benefits.map((benefit) => {
                      const IconComponent = iconMap[benefit.icon] || Package
                      return (
                        <span
                          key={benefit.label}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${
                            isAi
                              ? 'bg-stone-800 border-stone-700'
                              : 'bg-primary-50 border-primary-100'
                          }`}
                        >
                          <IconComponent
                            className={`w-3.5 h-3.5 ${
                              isAi ? 'text-green-400' : 'text-primary-600'
                            }`}
                          />
                          <span className={`text-xs font-medium ${
                            isAi ? 'text-stone-200' : 'text-primary-700'
                          }`}>
                            {benefit.label}
                          </span>
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Who it's for */}
              {audience && audience.length > 0 && (
                <div className="mb-8">
                  <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${
                    isAi ? 'text-stone-500' : 'text-stone-400'
                  }`}>
                    Who it&apos;s for
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {audience.map((item) => {
                      const IconComponent = iconMap[item.icon] || Package
                      return (
                        <div
                          key={item.title}
                          className={`p-3 rounded-xl border ${
                            isAi
                              ? 'bg-stone-800/50 border-stone-700'
                              : 'bg-primary-50/50 border-primary-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <IconComponent
                              className={`w-4 h-4 ${
                                isAi ? 'text-green-400' : 'text-primary-600'
                              }`}
                            />
                            <span className={`font-semibold text-sm ${
                              isAi ? 'text-stone-200' : 'text-stone-800'
                            }`}>
                              {item.title}
                            </span>
                          </div>
                          <p className={`text-xs leading-snug ${
                            isAi ? 'text-stone-400' : 'text-stone-500'
                          }`}>
                            {item.description}
                          </p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* CTA Block: Buttons row, then Ratings + Badge row */}
              <div className="space-y-3">
                {/* Buttons Row */}
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Button
                    href={cta.href}
                    external={cta.href.startsWith('http')}
                    variant={isAi ? 'primary' : 'primary'}
                    size="lg"
                  >
                    {cta.text}
                    <ArrowRight className="w-5 h-5" />
                  </Button>

                  <Button
                    href={isAi ? '/buddy-ai-assistant' : '/brand-partnerships'}
                    variant="ghost"
                    size="lg"
                    className={isAi ? 'text-stone-300 hover:text-white hover:bg-stone-800' : ''}
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Ratings + Badge Row */}
                {(appStore || badge) && (
                  <div className="flex flex-wrap items-center justify-center gap-4">
                    {appStore && (
                      <a
                        href={appStore.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 transition-colors ${
                          isAi
                            ? 'text-stone-400 hover:text-stone-200'
                            : 'text-stone-500 hover:text-stone-700'
                        }`}
                      >
                        <StarRating rating={appStore.rating} />
                        <span className="text-sm">
                          {appStore.rating} ({appStore.reviews} reviews)
                        </span>
                      </a>
                    )}

                    {badge && (
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
                        isAi
                          ? 'bg-stone-800 border border-stone-700'
                          : 'bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50'
                      }`}>
                        <Gem className={`w-3.5 h-3.5 ${isAi ? 'text-green-400' : 'text-emerald-600'}`} />
                        <span className={`text-xs font-semibold ${isAi ? 'text-green-400' : 'text-emerald-700'}`}>{badge}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Visual - aligned to top */}
            <motion.div
              initial={{ opacity: 0, x: reversed ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={reversed ? 'lg:order-1' : ''}
            >
              <Card
                variant="gradient"
                className="aspect-[4/3] flex items-center justify-center overflow-hidden"
              >
                {illustration ? (
                  <div className="w-full h-full">{illustration}</div>
                ) : (
                  <div className="text-center p-8">
                    <div
                      className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                        isAi
                          ? 'bg-gradient-to-br from-primary-500 to-primary-600'
                          : 'bg-gradient-to-br from-primary-500 to-primary-600'
                      }`}
                    >
                      {isAi ? (
                        <Bot className="w-10 h-10 text-white" />
                      ) : (
                        <Handshake className="w-10 h-10 text-white" />
                      )}
                    </div>
                    <p className="text-stone-500 text-sm">
                      App screenshot placeholder
                    </p>
                  </div>
                )}
              </Card>
            </motion.div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}
